import { AGWASuiteApiClientBase, type ApiResponse } from './agwasuite-api';
import { ApiFactory } from './agwasuite-api-factory';
import type {
  StateColourOperationResult,
  StateColourOverridesDto,
} from './agwa-api-model';

/**
 * The state colour override endpoints (state-colour-standard.md, slice B). Suite-level rather
 * than a module api: the colours belong to every module's badges. Reads ride the server's ETag
 * cache; writes are AGWA-admin-only and the server enforces it - the UI only decides whether
 * to SHOW the controls.
 */

const BASE_PATH = '/api/state-colours';

export class StateColoursApi extends AGWASuiteApiClientBase {
  async getOverrides(): Promise<ApiResponse<StateColourOverridesDto>> {
    return this.request<StateColourOverridesDto>(
      'GET',
      `${BASE_PATH}/overrides`
    );
  }

  /**
   * Set a label-level override: every state wearing the label changes at once. A null token
   * means "creating"; the server conflicts if an override already exists (or was reset under
   * an in-hand token), so two admins cannot silently overwrite each other.
   */
  async setLabelOverride(
    labelKey: string,
    colour: string,
    concurrencyToken: string | null
  ): Promise<ApiResponse<StateColourOperationResult>> {
    return this.request<StateColourOperationResult>(
      'PUT',
      `${BASE_PATH}/label/${encodeURIComponent(labelKey)}`,
      { colour, concurrencyToken }
    );
  }

  async resetLabelOverride(
    labelKey: string,
    concurrencyToken: string
  ): Promise<ApiResponse<StateColourOverridesDto>> {
    return this.request<StateColourOverridesDto>(
      'DELETE',
      `${BASE_PATH}/label/${encodeURIComponent(labelKey)}`,
      { concurrencyToken }
    );
  }

  /** Set a per-use override: one enum's one value, overriding its label's colour. */
  async setUseOverride(
    domainKey: string,
    valueKey: string,
    colour: string,
    concurrencyToken: string | null
  ): Promise<ApiResponse<StateColourOperationResult>> {
    return this.request<StateColourOperationResult>(
      'PUT',
      `${BASE_PATH}/use/${encodeURIComponent(domainKey)}/${encodeURIComponent(valueKey)}`,
      { colour, concurrencyToken }
    );
  }

  async resetUseOverride(
    domainKey: string,
    valueKey: string,
    concurrencyToken: string
  ): Promise<ApiResponse<StateColourOverridesDto>> {
    return this.request<StateColourOverridesDto>(
      'DELETE',
      `${BASE_PATH}/use/${encodeURIComponent(domainKey)}/${encodeURIComponent(valueKey)}`,
      { concurrencyToken }
    );
  }
}

export function getStateColoursApi(): StateColoursApi {
  return ApiFactory.authenticated(StateColoursApi);
}
